import React from "react";

// Shared bits for the Annual OKR coach dashboard. Mirrors
// `ExecutionBlueprintHelpers.js` — same progress bar, same arrow — so the two
// dashboards read as the same product rather than two designers' work.

// The eight stage milestones the coach dashboard walks through, in the order
// §6.1's `stage` enum uses for its `stage_index` (0–8). Structured exactly like
// `ExecutionBlueprintHelpers.MILESTONES` so the two dashboards are the same
// component with different data — which is what the coach sees.
//
// ⚠️ A LOCAL LIST *AND* THE SERVER'S `timestamps[]`, on purpose, and it took
// two passes to land here:
//   • An earlier version had a local list containing Guidance Notes and Leader
//     OKRs — both cut from scope (§10). Those are gone.
//   • The fix over-corrected into rendering `timestamps[]` directly, which made
//     the timeline's LENGTH depend on the payload: a row whose array arrived
//     short rendered a shorter timeline, so two clients' rows didn't line up
//     and a coach couldn't scan down the column. EXW hit this and solved it by
//     iterating a fixed list and looking timestamps up BY KEY. Same here.
//
// ⚠️ OUR WORDING WINS OVER THE SERVER'S (JK copy review G31, reversed
// 2026-09-29). `label` used to be only the fallback for a `timestamps[]` entry
// without its own; the Execution Blueprint dashboard settled the opposite way
// because the API's labels lag the product's vocabulary, and the two coach
// dashboards should read the same. "Sent to you" / "Review sent to CEO" are
// EXW's own labels for the same two moments.
export const MILESTONES = [
  { key: "SETUP_STARTED", label: "Annual OKRs started" },
  { key: "INTERVIEW_IN_PROGRESS", label: "Interview in progress" },
  { key: "SENT_TO_COACH", label: "Sent to you" },
  { key: "COACH_REVIEW_STARTED", label: "Review started" },
  { key: "COACH_REVIEW_COMPLETED", label: "Review sent to CEO" },
  { key: "LIVE_REVIEW_SCHEDULED", label: "Live review scheduled" },
  { key: "LIVE_REVIEW_STARTED", label: "Live review started" },
  { key: "LOCKED", label: "Locked" },
];

export const TOTAL_MILESTONES = MILESTONES.length;

export const ArrowRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3.5 8h9m0 0L9 4.5M12.5 8L9 11.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Eight segments, one per milestone, filled up to `filled` (the row's
// `stage_index`). Segments rather than a continuous bar because the journey is
// a fixed set of discrete steps, and a coach scanning the list wants to see
// WHICH step a client is on, not an approximate percentage.
//
// ⚠️ The count no longer comes from the payload. It used to take a `total` off
// `timestamps.length`, which meant two clients could render bars of different
// lengths in the same column — see MILESTONES above. Fixed at
// `TOTAL_MILESTONES`, exactly as EXW's bar is, so every row is comparable at a
// glance.
//
// `filled` is clamped because §6.1 gives `stage_index` as "0–8" without saying
// whether it is 0- or 1-based; an off-by-one should never render more segments
// than exist. The `current` segment marks the step in progress, matching EXW.
export const AnnualOkrProgressBar = ({ filled = 0 }) => {
  const clamped = Math.max(0, Math.min(filled, TOTAL_MILESTONES));
  const isComplete = clamped >= TOTAL_MILESTONES;

  return (
    <div
      className="aok-progress"
      role="img"
      aria-label={`Step ${clamped} of ${TOTAL_MILESTONES}`}
    >
      {Array.from({ length: TOTAL_MILESTONES }).map((_, index) => {
        const segmentIndex = index + 1;
        const isFilled = segmentIndex <= clamped;
        const isCurrent = !isComplete && segmentIndex === clamped + 1;
        return (
          <span
            key={index}
            // `aok-progress-segment` / `filled` / `current` — the blueprint
            // bar's own class names, because this file's stylesheet is now a
            // prefix-for-prefix copy of the blueprint's (see AnnualOkr.scss).
            // The old `aok-progress-seg` + `is-filled` naming had no matching
            // rule there and rendered as unstyled empty spans.
            className={`aok-progress-segment ${isFilled ? "filled" : ""} ${
              isCurrent ? "current" : ""
            }`}
          />
        );
      })}
    </div>
  );
};
